import _cloneDeep from 'lodash/cloneDeep'
import { getOrdererLevelArray } from 'frontend/helpers/perimeterUtils'
import { store } from 'frontend/store/store'
import { VISUAL_STANDARD_PATTERN_LEVEL } from 'frontend/constants/constants'
import { VT_RESULTS } from 'frontend/constants/callers'

const PATTERN = 0
const STANDARD = 1
const VARIANT = 2
const SKILL_MATRIX_ROUTE_RE = /^skill-matrix-*/

let descendants = []

/**
 * @typedef  {{id: number, name: string, parent: number | null}} FactoryLevelItem
 */
export default class FactoryLevel {

  static get PATTERN () { return PATTERN }

  static get STANDARD () { return STANDARD }

  static get VARIANT () { return VARIANT }

  /**
   * @constructor
   * @param {number} id - Pattern id
   * @param {number} type - The factory level type (PATTERN, STANDARD, VARIANT)
   * @param {string | undefined} name - Pattern name
   * @param {number | null} parent - The parent of the FactoryLevel
   * @param {string[]} callers - FactoryLevel callers
   * @param {FactoryLevelItem[]} items - FactoryLevel perimeter items
   * @param {FactoryLevelItem[]} filteredItems - FactoryLevel filtered perimeter items
   * @param {FactoryLevelItem['id'] | FactoryLevelItem['id'][]} selectedItemId - FactoryLevel selected item id
   *
   */
  constructor (id, type, name, parent, callers, items, filteredItems, selectedItemId) {
    /** @type {number} */
    this.id = id
    /** @type {number} */
    this.type = type
    /** @type {string|undefined} */
    this.name = name
    /** @type {number|null} */
    this.parent = parent
    /** @type {string[]} */
    this.callers = callers
    /** @type {FactoryLevelItem[]} */
    this.items = items
    /** @type {FactoryLevelItem[]} */
    this.filteredItems = filteredItems
    /** @type {FactoryLevelItem['id'] | FactoryLevelItem['id'][]} */
    this.selectedItemIds = Array.isArray(selectedItemId) ? selectedItemId : [selectedItemId]
  }

  /**
   *
   * @param {FactoryLevelItem} item - FactoryLevel perimeter item to be added
   */
  addItem (item) {
    if (this.items.some(i => i.id === item.id && i.parent === item.parent)) { return }
    this.items.push(item)
  }

  addCaller (caller) {
    this.callers.push(caller)
  }

  addFilteredItem (item) {
    this.filteredItems.push(item)
  }

  resetItems () {
    this.filteredItems = []
  }

  getFilteredItemIds () {
    return this.filteredItems.map((item) => { return item.id })
  }

  /**
   *
   * @param {FactoryLevelItem['id'] | FactoryLevelItem['id'][]} selectedItemIds
   */
  setSelectedItemIds (selectedItemIds) {
    this.selectedItemIds = Array.isArray(selectedItemIds) ? selectedItemIds : [selectedItemIds]
  }

  areAllSelected () {
    return (this.selectedItemIds.length === 1 && this.selectedItemIds[0] === 0) || this.selectedItemIds.length === 0
  }

  getSelectedItemIds () {
    if (this.areAllSelected()) {
      return this.filteredItems.map((item) => { return item.id })
    }
    return Array.isArray(this.selectedItemIds) ? this.selectedItemIds : [this.selectedItemIds]
  }

  getSelectedItems () {
    if (this.areAllSelected()) return []
    return this.selectedItemIds.map((itemId) => {
      return this.getItemById(itemId)
    })
  }

  getItemById (itemId) {
    return this.normalize(this.items.find((item) => { return item.id === itemId }))
  }

  getSelectedParentItemIds () {
    if (this.parent === null) {
      return []
    }
    const parent = FactoryLevel.getFactoryLevelByPatternId(this.parent)
    if (parent.areAllSelected() || this.selectedItemIds.length === 1 && this.selectedItemIds[0] === null) {
      return parent.filteredItems.map((item) => item.id)
    }
    return Array.isArray(parent.selectedItemIds) ? parent.selectedItemIds : [parent.selectedItemIds]
  }

  getParent () {
    const factoryLevels = store.getters.getFactoryLevels
    return factoryLevels.find(factory => factory.id === this.parent)
  }

  getChildren () {
    const factoryLevels = store.getters.getFactoryLevels
    return factoryLevels.filter((factoryLevel) => {
      return factoryLevel.parent !== null && factoryLevel.parent === this.id
    })
  }

  getChildrens (childrens=[]) {
    this.getChildren().forEach(children => {
      childrens.push(children)
      if (children.getChildren().length > 0) {
        children.getChildrens(childrens)
      }
    })
    return childrens
  }

  normalize (response) {
    return (response === undefined || response === null) ? null : response
  }

  static getOrderedFactoryLevels () {
    const factoryLevels = []
    getOrdererLevelArray(store.getters.getFactoryLevels, factoryLevels, null, 0)
    return factoryLevels
  }

  static getFactoryLevelsFilteredByCaller (caller, routeName) {
    if (!caller) return []
    const factoryLevels = []
    let currentFactoryLevel = store.getters.getFactoryLevels.find((item) => { return item.callers.includes(caller) })
    factoryLevels.push(currentFactoryLevel)
    while (currentFactoryLevel?.parent != null) {
      currentFactoryLevel = store.getters.getFactoryLevels.find((item) => { return item.id === currentFactoryLevel.parent })
      factoryLevels.push(currentFactoryLevel)
    }
    factoryLevels.reverse()
    const variantMatchingPaths = ['variant-viewer']
    if (caller === VT_RESULTS && routeName && variantMatchingPaths.includes(routeName)) {
      factoryLevels.push(FactoryLevel.getFactoryLevelsFilteredByType(FactoryLevel.VARIANT)[0])
    }
    if (SKILL_MATRIX_ROUTE_RE.test(routeName) && !store.getters.getSkillMatrix.EVALUATE_STANDARDS) {
      factoryLevels.pop()
    }
    return factoryLevels
  }

  /**
   * @param { number } perimeterId
   * @returns { string[] }
   */
  static getPerimeterParentsNameById (perimeterId) {
    const allPerimeters = store.getters.getPerimeters
    const tracedPerimeters = []
    let selectedPerimeter = allPerimeters.find((p) => { return p.id === perimeterId })
    while (selectedPerimeter !== undefined) {
      let name = selectedPerimeter.name
      if (selectedPerimeter.description) name += ' ' + selectedPerimeter.description
      tracedPerimeters.unshift(name)
      selectedPerimeter = allPerimeters.find((p) => { return p.id === selectedPerimeter.parent })
    }
    return tracedPerimeters
  }

  static getPerimeterParentsById (perimeterId) {
    const allPerimeters = store.getters.getPerimeters
    const tracedPerimeters = []
    let selectedPerimeter = allPerimeters.find((p) => { return p.id === perimeterId })
    while (selectedPerimeter !== undefined) {
      const perimeter = selectedPerimeter
      tracedPerimeters.unshift(perimeter)
      selectedPerimeter = allPerimeters.find((p) => { return p.id === selectedPerimeter.parent })
    }
    return tracedPerimeters
  }

  static getTopLevel (caller) {
    return FactoryLevel.getFactoryLevelsFilteredByCaller(caller).find((factoryLevel) => factoryLevel?.parent == null)
  }

  static getPerimetersIdsTopLevel (caller) {
    return FactoryLevel.getTopLevel(caller).items.map(item => item.id)
  }

  static getLowestLevel (caller) {
    return FactoryLevel.getFactoryLevelsFilteredByCaller(caller)[FactoryLevel.getFactoryLevelsFilteredByCaller(caller).length - 1]
  }

  static getLowestLevelSelected (caller) {
    let lowestLevelSelected = null
    FactoryLevel.getFactoryLevelsFilteredByCaller(caller).forEach((level) => {
      if (level && !level.areAllSelected()) {
        lowestLevelSelected = level
      }
    })
    return lowestLevelSelected
  }

  static getLowestFactoryParents (caller) {
    const lowestLevel = FactoryLevel.getLowestLevel(caller)
    return FactoryLevel.getAscendants(lowestLevel)
  }

  static getFactoryLevelByPatternId (patternId, factoryLevels = null) {
    if (factoryLevels === null) {
      factoryLevels = store.getters.getFactoryLevels
    }
    return factoryLevels.find((factoryLevel) => { return factoryLevel.id === patternId })
  }

  static getFactoryLevelIndexByPatternId (caller, patternId) {
    return FactoryLevel.getFactoryLevelsFilteredByCaller(caller).findIndex((factoryLevel) => { return factoryLevel.id === patternId })
  }

  static getFactoryLevelByCaller (caller, factoryLevels = null) {
    if (factoryLevels === null) {
      factoryLevels = store.getters.getFactoryLevels
    }
    return factoryLevels.find((factoryLevel) => { return factoryLevel.callers.includes(caller) })
  }

  static getFactoryLevelForPerimeterId (perimeterId) {
    const factoryLevels = store.getters.getFactoryLevels
    return factoryLevels.find((factoryLevel) => { return factoryLevel.items.some((p) => { return p.id === perimeterId }) })
  }

  static getAscendants (factoryLevel) {
    const factoryParents = []
    factoryParents.push(factoryLevel)
    while (factoryLevel.parent !== null) {
      factoryLevel = factoryLevel.getParent()
      factoryParents.push(factoryLevel)
    }
    return factoryParents.reverse()
  }

  static getDescendants (factoryLevel) {
    factoryLevel.getChildren().forEach((level) => {
      if (level.id !== VISUAL_STANDARD_PATTERN_LEVEL) {
        const filteredItems = level.items.filter(item => {
          return factoryLevel.items.find((ascendantItem) => { return ascendantItem.id === item.parent }) !== undefined
        })
        const filteredLevel = _cloneDeep(level)
        filteredLevel.items = filteredItems
        descendants.push(filteredLevel)
        this.getDescendants(filteredLevel)
      }
    })
  }

  static getFactoryTreeByItems (perimeterIds) {
    const factoryTree = []
    const patterns = FactoryLevel.getOnlyPatterns(store.getters.getFactoryLevels)
    for (const level of patterns) {
      const filteredPerimeters = level.items.filter((perimeter) => {
        return perimeterIds.some(id => id === perimeter.id )
      })
      const filteredInitialLevel = _cloneDeep(level)
      filteredInitialLevel.items = filteredPerimeters
      if (filteredPerimeters.length > 0) factoryTree.push(filteredInitialLevel)
    }
    return factoryTree
  }

  static getFactoryTreeByItemsWithDescendants (perimeterIds, factoryStructure = null) {
    const structure = factoryStructure === null ? store.getters.getFactoryLevels : factoryStructure
    const factoryTree = []
    for (const level of store.getters.getFactoryLevels) {
      const filteredPerimeters = level.items.filter((perimeter) => {
        return perimeterIds.some(id => id === perimeter.id )
      })
      const filteredInitialLevel = _cloneDeep(level)
      filteredInitialLevel.items = filteredPerimeters
      if (filteredPerimeters.length > 0) {
        factoryTree.push(filteredInitialLevel)
        descendants = []
        this.getDescendants(filteredInitialLevel)
        descendants.forEach((descendant) => {
          if (structure.map(item => item.id).includes(descendant.id)) {
            factoryTree.push(descendant)
          }
        })
      }
    }
    return factoryTree
  }

  /**
   * @param {Array.<number>} perimeterIds
   * @param {(Array.<Object> | null)} factoryStructure
   */
  static getFactoryTreeByLevelItems (perimeterIds, factoryStructure = null) {
    const factoryTree = []
    const structure = factoryStructure === null ? store.getters.getFactoryLevels : factoryStructure
    const initialLevel = structure.filter((level) => {
      return level.type === PATTERN
    }).find((level) => {
      return level.items.some(perimeter => perimeterIds.includes(perimeter.id))
    })
    const filteredPerimeters = initialLevel.items.filter((perimeter) => {
      return perimeterIds.some(id => id === perimeter.id )
    })
    const filteredInitialLevel = _cloneDeep(initialLevel)
    filteredInitialLevel.items = filteredPerimeters
    factoryTree.push(filteredInitialLevel)
    descendants = []
    this.getDescendants(filteredInitialLevel)
    descendants.forEach((descendant) => {
      if (structure.map(item => item.id).includes(descendant.id)) {
        factoryTree.push(descendant)
      }
    })
    return factoryTree
  }

  static getFactoryLevelIndexByCaller (caller) {
    return store.getters.getFactoryLevels.findIndex((factoryLevel) => { return factoryLevel.callers.includes(caller) })
  }

  static getFactoryLevelsFilteredByType (type) {
    return store.getters.getFactoryLevels.filter((factoryLevel) => { return factoryLevel.type === type })
  }

  static getOnlyPatterns (factoryLevels) {
    return factoryLevels.filter((factoryLevel) => { return factoryLevel.type === FactoryLevel.PATTERN })
  }

  static getFactoryLevelFilteredByParent (parentFactoryId) {
    return store.getters.getFactoryLevels.find((level) => level.parent === parentFactoryId)
  }

  static resetSelections () {
    const factoryLevels = store.getters.getFactoryLevels
    factoryLevels.forEach((factoryLevel) => {
      factoryLevel.selectedItemIds = []
    })
  }

}
